import { useState, type FormEvent } from 'react'
import { apiPost, ApiError } from '../lib/api.ts'
import { takeSignedOutNotice, useRefreshSession } from '../lib/session.ts'
import { AuthLayout, FormError, buttonClass, inputClass } from './AuthLayout.tsx'

/**
 * There is no "create an account" link, and there never will be: accounts exist
 * only by invitation (§6c). A signup affordance here would be a lie.
 */
export function LoginScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice] = useState(takeSignedOutNotice)
  const refresh = useRefreshSession()

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await apiPost('/auth/login', { email, password })
      // The server decides what comes next — challenge or enrolment. Re-reading
      // the session is what routes us, rather than the client guessing.
      await refresh()
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 429
          ? 'Too many attempts. This sign-in is locked for 30 minutes.'
          : 'That email and password combination was not recognised.',
      )
      setBusy(false)
    }
  }

  return (
    <AuthLayout title="Sign in" subtitle="Blocwork accounts are created by invitation only.">
      {notice ? <p role="status" className="mb-4 rounded-md bg-surface-sunken px-3 py-2 text-sm text-ink">{notice}</p> : null}
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-ink">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-ink">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
        </div>

        <button type="submit" disabled={busy} className={buttonClass}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <FormError message={error} />

      <p className="mt-4 text-xs text-ink-muted">
        Every account requires a second factor. You will be asked for it next.
      </p>
    </AuthLayout>
  )
}
